import { motion } from "framer-motion";
import { Clock, Zap } from "lucide-react";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

function getTimeLeft(dateStr) {
  const now = new Date();
  const end = new Date(dateStr);
  const diff = end - now;
  if (diff <= 0) return "Abgelaufen";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
}

export default function WeeklyChallenge({ challenge }) {
  const { title, description, reward, progress, maxProgress, endsAt, emoji, type } = challenge;
  const progressPercent = (progress / maxProgress) * 100;
  const isComplete = progress >= maxProgress;
  const timeLeft = getTimeLeft(endsAt);

  const typeColors = {
    order: C.green,
    visit: C.orange,
    product: C.tan,
    social: "#8B4C70",
  };
  const accentColor = typeColors[type] || C.green;

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      className="p-4 relative overflow-hidden"
      style={{
        borderRadius: 20,
        background: C.bgCard,
        boxShadow: BRAND.shadows.sm,
        borderLeft: `4px solid ${accentColor}`,
      }}
    >
      {/* Complete overlay */}
      {isComplete && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[20px]"
          style={{ backgroundColor: C.green + "15", zIndex: 1 }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 12 }}
            className="text-center">
            <span className="text-4xl block mb-2">🎉</span>
            <p className="text-[14px] font-bold" style={{ color: C.green }}>
              Geschafft!
            </p>
            <p className="text-[12px]" style={{ color: C.textMuted }}>
              {reward}
            </p>
          </motion.div>
        </div>
      )}

      <div className="flex items-start gap-3">
        {/* Emoji */}
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accentColor + "12" }}>
          <span className="text-xl">{emoji}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-[14px] font-bold"
              style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
              {title}
            </h4>
            {/* Timer */}
            <div className="flex items-center gap-1">
              <Clock size={12} style={{ color: C.textMuted }} />
              <span className="text-[11px] font-medium" style={{ color: C.textMuted }}>
                {timeLeft}
              </span>
            </div>
          </div>

          <p className="text-[12px] leading-snug" style={{ color: C.textMuted }}>
            {description}
          </p>

          {/* Progress */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold" style={{ color: accentColor }}>
                {progress}/{maxProgress}
              </span>
              <span className="text-[11px] font-medium" style={{ color: C.textMuted }}>
                {Math.round(progressPercent)}%
              </span>
            </div>
            <div className="w-full h-2 rounded-full" style={{ backgroundColor: C.border }}>
              <motion.div
                className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                style={{ backgroundColor: accentColor }}
              />
            </div>
          </div>

          {/* Reward */}
          <div className="flex items-center gap-1.5 mt-2.5">
            <Zap size={12} style={{ color: accentColor }} fill={accentColor} />
            <span className="text-[11px] font-semibold" style={{ color: accentColor }}>
              {reward}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
